import LinearMotionImg from '../../Images/LinearmotionImg.jpg'
// ─── Shared styles — earthy orange palette ─────────────────────────
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia',serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New',monospace", fontSize:'0.88rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#a04010', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New',monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it,i)=><div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}
function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{...S.th,width:'220px'}}>Concept</th>
        <th style={S.th}>Detail</th>
      </tr></thead>
      <tbody>{rows.map(([l,v],i)=>(
        <tr key={i}>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.tdL}}>{l}</td>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.code}}>{v}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ─── Helper: draw a helix band (screw thread schematic) ──────────────
function helixBand(x0, y0, length, r, pitch, nTurns, fill, stroke) {
  // Draws a simplified trapezoidal-thread screw as a series of parallelogram bands
  const pts = [];
  const steps = nTurns * 20;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const bx = x0 + t * length;
    const band = Math.sin(2 * Math.PI * nTurns * t);
    pts.push([bx, y0 + r * 0.45 * band]);
  }
  return pts;
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Lead Screw vs Ball Screw vs Linear Rail — Side-by-Side
// ─────────────────────────────────────────────────────────────────────
function DiagramComparison() {
  const W = 960, H = 420;

  // Draw a simplified lead screw cross section
  const LeadScrew = ({ x, y }) => {
    const L = 180, r = 14, th = 10;
    const nTurns = 5;
    return (
      <g>
        {/* Shaft body */}
        <rect x={x} y={y - r} width={L} height={r * 2} rx={r} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        {/* Thread teeth */}
        {Array.from({length: nTurns * 2}, (_, i) => {
          const bx = x + 8 + i * (L - 16) / (nTurns * 2);
          const isUp = i % 2 === 0;
          return (
            <g key={i}>
              <rect x={bx} y={isUp ? y - r - th : y + r} width={12} height={th} rx={2}
                fill="#c45a10" stroke="#7a2e00" strokeWidth="1.2" opacity="0.9"/>
            </g>
          );
        })}
        {/* Nut body */}
        <rect x={x + 60} y={y - r - th - 8} width={40} height={(r + th + 8) * 2}
          rx={5} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2" opacity="0.88"/>
        <text x={x + 80} y={y + 5} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">NUT</text>
        {/* Sliding friction indicator */}
        <path d={`M ${x+80} ${y-r-th-16} L ${x+72} ${y-r-th-28} L ${x+88} ${y-r-th-28} Z`}
          fill="#c0392b" opacity="0.7"/>
        <text x={x + 80} y={y - r - th - 30} textAnchor="middle" fontSize="8" fill="#c0392b">friction</text>
      </g>
    );
  };

  // Draw a ball screw
  const BallScrew = ({ x, y }) => {
    const L = 180, r = 14, th = 10;
    const nTurns = 5;
    const nBalls = 8;
    return (
      <g>
        {/* Shaft with groove */}
        <rect x={x} y={y - r} width={L} height={r * 2} rx={r} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        {/* Thread groove */}
        {Array.from({length: nTurns * 2}, (_, i) => {
          const bx = x + 8 + i * (L - 16) / (nTurns * 2);
          return (
            <g key={i}>
              <rect x={bx} y={y - r - th} width={10} height={th} rx={2}
                fill="#c45a10" stroke="#7a2e00" strokeWidth="1.2" opacity="0.85"/>
              <rect x={bx} y={y + r} width={10} height={th} rx={2}
                fill="#c45a10" stroke="#7a2e00" strokeWidth="1.2" opacity="0.85"/>
            </g>
          );
        })}
        {/* Ball nut housing */}
        <rect x={x + 55} y={y - r - th - 10} width={50} height={(r + th + 10) * 2}
          rx={5} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2" opacity="0.88"/>
        {/* Balls inside nut */}
        {Array.from({length: nBalls}, (_, i) => {
          const bx = x + 60 + i * 5;
          const by = y + (i % 2 === 0 ? -r + 2 : r - 2);
          return <circle key={i} cx={bx} cy={by} r={4} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.2"/>;
        })}
        {/* Return tube indicator */}
        <path d={`M ${x+60} ${y-r-th-8} Q ${x+80} ${y-r-th-22} ${x+100} ${y-r-th-8}`}
          fill="none" stroke="#c45a10" strokeWidth="2.5" strokeDasharray="4,2"/>
        <text x={x + 80} y={y - r - th - 24} textAnchor="middle" fontSize="8" fill="#c45a10">return</text>
        {/* Rolling friction indicator */}
        <path d={`M ${x+80} ${y+r+th+10} L ${x+72} ${y+r+th+22} L ${x+88} ${y+r+th+22} Z`}
          fill="#27ae60" opacity="0.7"/>
        <text x={x + 80} y={y + r + th + 26} textAnchor="middle" fontSize="8" fill="#27ae60">rolling</text>
      </g>
    );
  };

  // Draw a linear rail + carriage
  const LinearRail = ({ x, y }) => {
    const L = 180, railH = 22, railW = L, carriageW = 64, carriageH = 42;
    return (
      <g>
        {/* Rail profile */}
        <rect x={x} y={y - railH / 2} width={railW} height={railH}
          rx={4} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        {/* Rail groove (simplified) */}
        <rect x={x + 6} y={y - railH / 2 + 4} width={railW - 12} height={6}
          rx={2} fill="#c45a10" opacity="0.5"/>
        <rect x={x + 6} y={y + railH / 2 - 10} width={railW - 12} height={6}
          rx={2} fill="#c45a10" opacity="0.5"/>
        {/* Carriage block */}
        <rect x={x + 60} y={y - railH / 2 - 14} width={carriageW} height={carriageH}
          rx={4} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2" opacity="0.9"/>
        {/* Balls inside carriage (top + bottom rows) */}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <g key={i}>
            <circle cx={x + 67 + i * 9} cy={y - railH / 2 - 4} r={4.5}
              fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.2"/>
            <circle cx={x + 67 + i * 9} cy={y + railH / 2 - 4} r={4.5}
              fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.2"/>
          </g>
        ))}
        <text x={x + 92} y={y + 4} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">CARRIAGE</text>
        {/* Load arrow */}
        <line x1={x + 92} y1={y - railH / 2 - 28} x2={x + 92} y2={y - railH / 2 - 18}
          stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arLM)"/>
        <text x={x + 92} y={y - railH / 2 - 32} textAnchor="middle" fontSize="8" fill="#c45a10">F_load</text>
        {/* High stiffness indicator */}
        <text x={x + 92} y={y + railH / 2 + 22} textAnchor="middle" fontSize="8" fill="#7a2e00" fontWeight="700">High stiffness</text>
        <text x={x + 92} y={y + railH / 2 + 33} textAnchor="middle" fontSize="8" fill="#a04010">4-dir load capacity</text>
      </g>
    );
  };

  const colW = 290;
  const panels = [
    { label: 'Lead Screw', sub: 'Sliding contact · simple · self-locking', x: 50 },
    { label: 'Ball Screw', sub: 'Recirculating balls · high efficiency', x: 340 },
    { label: 'Linear Rail + Carriage', sub: 'Recirculating balls · high load · stiff', x: 630 },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linear Motion Systems — Lead Screw vs Ball Screw vs Linear Rail Comparison</div>
        
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="arLM" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {panels.map((p, i) => (
          <g key={i}>
            <rect x={p.x - 10} y={10} width={colW - 10} height={400} rx={6}
              fill="none" stroke={i === 1 ? '#c45a10' : '#e8b07a'}
              strokeWidth={i === 1 ? 2 : 1.5} strokeDasharray={i === 1 ? '' : '5,3'}/>
            <text x={p.x + (colW - 20) / 2} y={32} textAnchor="middle"
              fontSize="12" fontWeight="700" fill="#7a2e00">{p.label}</text>
            <text x={p.x + (colW - 20) / 2} y={46} textAnchor="middle"
              fontSize="8.5" fill="#a04010">{p.sub}</text>
          </g>
        ))}

        <LeadScrew x={64} y={120} />
        <BallScrew x={350} y={120} />
        <LinearRail x={640} y={120} />

        {/* Comparison rows */}
        {[
          ['Friction type', 'Sliding (μ≈0.1–0.3)', 'Rolling (μ≈0.003)', 'Rolling (μ≈0.002)'],
          ['Efficiency', '25–85%', '90–96%', '> 95%'],
          ['Backlash', 'Moderate (need preload)', 'Low (preload optional)', 'Near zero (preloaded)'],
          ['Self-locking', 'Yes (lead angle < friction)', 'No — not self-locking', 'N/A (no screw)'],
          ['Speed capability', 'Low–medium', 'High (DmN up to 150k)', 'Very high'],
          ['Accuracy', 'Medium (±0.05–0.1mm)', 'High (±0.003–0.05mm)', 'Very high (±0.001mm)'],
          ['Cost', 'Low', 'Medium–high', 'Medium + drive needed'],
          ['Lubrication', 'Grease or oil (heavy)', 'Grease (light)', 'Grease (very light)'],
          ['Noise', 'Moderate', 'Low', 'Very low'],
        ].map((row, ri) => {
          const rowY = 210 + ri * 21;
          return (
            <g key={ri}>
              <rect x={10} y={rowY} width={940} height={21}
                fill={ri % 2 === 0 ? '#fdf4ec' : '#fdf0e4'}/>
              <text x={14} y={rowY + 14} fontSize="8.5" fontWeight="700" fill="#7a2e00">{row[0]}</text>
              <text x={260} y={rowY + 14} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">{row[1]}</text>
              <text x={550} y={rowY + 14} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#c45a10">{row[2]}</text>
              <text x={840} y={rowY + 14} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#7a2e00">{row[3]}</text>
              {ri === 0 && (
                <>
                  <text x={260} y={rowY - 6} fontSize="8" fontWeight="700" fill="#a04010">Lead Screw</text>
                  <text x={550} y={rowY - 6} fontSize="8" fontWeight="700" fill="#c45a10">Ball Screw</text>
                  <text x={840} y={rowY - 6} fontSize="8" fontWeight="700" fill="#7a2e00">Linear Rail</text>
                </>
              )}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Lead screws: simple and self-locking but low efficiency · ball screws: high efficiency and precision but no self-lock · linear rails: highest stiffness and accuracy for guided motion</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Lead Screw Anatomy & Thread Profiles
// ─────────────────────────────────────────────────────────────────────
function DiagramLeadScrew() {
  const W = 960, H = 400;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Lead Screw Anatomy — Thread Profiles, Geometry &amp; Self-Locking Condition</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="arLS2" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arLS2r" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
            <polygon points="6 0,0 3.5,6 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* ── Main screw cross-section schematic ── */}
        {/* Shaft */}
        <rect x={30} y={148} width={360} height={30} rx={4} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        {/* Thread crests above */}
        {Array.from({length: 12}, (_, i) => (
          <g key={i}>
            <polygon points={`${36 + i*28},148  ${44 + i*28},148  ${48 + i*28},128  ${32 + i*28},128`}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="1.3" opacity="0.9"/>
            <polygon points={`${36 + i*28},178  ${44 + i*28},178  ${48 + i*28},198  ${32 + i*28},198`}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="1.3" opacity="0.9"/>
          </g>
        ))}
        {/* Nut */}
        <rect x={130} y={114} width={80} height={98} rx={5}
          fill="#e8b07a" stroke="#7a2e00" strokeWidth="2.5" opacity="0.85"/>
        <text x={170} y={165} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">NUT</text>
        <text x={170} y={178} textAnchor="middle" fontSize="8.5" fill="#a04010">(sliding)</text>

        {/* Dimension arrows */}
        {/* Lead (pitch * starts) */}
        <line x1={36} y1={210} x2={36 + 56} y2={210} stroke="#c45a10" strokeWidth="1.5"
          markerEnd="url(#arLS2)" markerStart="url(#arLS2r)"/>
        <text x={64} y={224} textAnchor="middle" fontSize="9" fontWeight="700" fill="#c45a10">Lead (L) = pitch × starts</text>

        {/* Thread pitch */}
        <line x1={36} y1={236} x2={36 + 28} y2={236} stroke="#7a2e00" strokeWidth="1.5"
          markerEnd="url(#arLS2)" markerStart="url(#arLS2r)"/>
        <text x={50} y={250} textAnchor="middle" fontSize="8.5" fill="#7a2e00">Pitch p</text>

        {/* Major & minor diameter */}
        <line x1={14} y1={128} x2={14} y2={198} stroke="#a04010" strokeWidth="1.5"
          markerEnd="url(#arLS2)" markerStart="url(#arLS2r)"/>
        <text x={10} y={165} textAnchor="end" fontSize="8" fill="#a04010" transform="rotate(-90,10,165)">d_major</text>
        <line x1={395} y1={148} x2={395} y2={178} stroke="#7a2e00" strokeWidth="1.5"
          markerEnd="url(#arLS2)" markerStart="url(#arLS2r)"/>
        <text x={408} y={165} fontSize="8" fill="#7a2e00">d_minor</text>

        {/* Labels */}
        <text x={170} y={104} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Lead Screw (ACME shown)</text>
        <text x={170} y={116} textAnchor="middle" fontSize="8.5" fill="#a04010">thread flank angle 14.5° (ACME) or 15° (metric trapezoidal)</text>

        {/* ── Thread profile comparison panels ── */}
        {/* Profiles at top of right section */}
        {[
          { label: 'Square', sx: 450, color: '#c45a10', pts: '0,0 20,0 20,20 0,20', desc: 'Best efficiency · no radial force\nHard to machine · not self-centering\nUsed: precision toolroom' },
          { label: 'ACME/Tr', sx: 570, color: '#a04010', pts: '4,0 20,0 24,20 0,20', desc: '29° (ACME) or 30° (Tr) flank angle\nGood strength · machinable\nMost common lead screw thread' },
          { label: 'Buttress', sx: 690, color: '#d4874a', pts: '0,0 20,0 22,20 2,20', desc: 'One-direction thrust load\n7° / 45° flanks · asymmetric\nJacks, presses, one-way loads' },
          { label: 'Ball Groove', sx: 810, color: '#7a2e00', pts: '', desc: 'Gothic arch / circular arc\n≈50% contact angle · preloadable\nBall screws only' },
        ].map((prof, i) => (
          <g key={i}>
            <rect x={prof.sx - 10} y={18} width={115} height={100} rx={5}
              fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1.5"/>
            <text x={prof.sx + 47} y={34} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">{prof.label}</text>
            {/* Thread profile shape */}
            {prof.pts ? (
              <polygon points={prof.pts.split(' ').map(p => {
                const [px, py] = p.split(',');
                return `${prof.sx + parseInt(px)},${50 + parseInt(py)}`;
              }).join(' ')}
                fill={prof.color} stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
            ) : (
              <path d={`M ${prof.sx} 55 A 14 14 0 0 1 ${prof.sx + 24} 55`}
                fill={prof.color} stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
            )}
            {prof.desc.split('\n').map((line, li) => (
              <text key={li} x={prof.sx + 47} y={84 + li * 12} textAnchor="middle"
                fontSize="7.5" fill="#7a4a28">{line}</text>
            ))}
          </g>
        ))}

        {/* ── Self-locking & efficiency panel ── */}
        <rect x={440} y={125} width={508} height={260} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={694} y={143} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Self-Locking Condition &amp; Efficiency</text>
        <line x1={448} y1={149} x2={940} y2={149} stroke="#e8b07a"/>
        {[
          ['Lead angle λ:', 'tan(λ) = L / (π · d_mean)'],
          ['', 'L = lead (mm)  ·  d_mean = mean thread dia'],
          ['', ''],
          ['Self-locking:', 'λ < φ  (lead angle < friction angle)'],
          ['Friction angle:', 'φ = arctan(μ / cos(α))'],
          ['', 'α = half-thread-flank angle'],
          ['', 'μ = friction coefficient'],
          ['', ''],
          ['Forward η:', 'η_f = tan(λ) / tan(λ + φ)'],
          ['', '(rotating screw → moving nut)'],
          ['Back-drive η:', 'η_b = tan(λ - φ) / tan(λ)'],
          ['', 'Negative if self-locking (λ < φ)'],
          ['', ''],
          ['Torque to raise:', 'T = F·d_mean/2 · tan(λ + φ)'],
          ['Torque to lower:', 'T = F·d_mean/2 · tan(φ - λ)'],
          ['', '(negative = braking torque)'],
          ['', ''],
          ['Typical values:', 'ACME, μ=0.15 → η ≈ 35–65%'],
          ['', 'ACME, μ=0.08 → η ≈ 55–80%'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={448} y={162 + i * 12} fontSize="9"
              fontWeight={l ? '700' : '400'} fill={l === 'Self-locking:' ? '#c45a10' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            <text x={l ? 566 : 448} y={162 + i * 12} fontSize="9"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Lead angle governs both efficiency and self-locking — shallow angles (small lead/dia) give self-locking but low efficiency; steep angles give high efficiency but require a brake to hold position</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Ball Screw Anatomy
// ─────────────────────────────────────────────────────────────────────
function DiagramBallScrew() {
  const W = 960, H = 400;
  const cx = 300, cy = 195; // centre of ball nut cross-section
  const shaftR = 22, nutOuter = 50, nutInner = 28;
  const ballR = 7, ballPcd = (shaftR + nutInner + 4); // ball pitch circle

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Ball Screw — Complete Anatomy, Thread Geometry &amp; Preload Methods</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="arBS" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arBSr" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
            <polygon points="6 0,0 3.5,6 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* ── Shaft side view (left side) ── */}
        {/* Shaft body */}
        <rect x={30} y={cy - shaftR} width={340} height={shaftR * 2} rx={shaftR}
          fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5"/>
        {/* Thread groove (simplified sinusoid) */}
        {Array.from({length: 40}, (_, i) => {
          const t = i / 39;
          const x1 = 30 + t * 340;
          const x2 = 30 + (t + 1 / 39) * 340;
          const y1 = cy - shaftR + 4 + 10 * Math.sin(2 * Math.PI * 6 * t);
          const y2 = cy - shaftR + 4 + 10 * Math.sin(2 * Math.PI * 6 * (t + 1 / 39));
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#7a2e00" strokeWidth="1.5" opacity="0.5"/>;
        })}
        {Array.from({length: 40}, (_, i) => {
          const t = i / 39;
          const x1 = 30 + t * 340;
          const x2 = 30 + (t + 1 / 39) * 340;
          const y1 = cy + shaftR - 4 + 10 * Math.sin(2 * Math.PI * 6 * t + Math.PI);
          const y2 = cy + shaftR - 4 + 10 * Math.sin(2 * Math.PI * 6 * (t + 1 / 39) + Math.PI);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#7a2e00" strokeWidth="1.5" opacity="0.5"/>;
        })}

        {/* Ball nut outer */}
        <rect x={130} y={cy - nutOuter} width={110} height={nutOuter * 2} rx={8}
          fill="#e8b07a" stroke="#7a2e00" strokeWidth="2.5" opacity="0.9"/>
        {/* Return tube */}
        <path d={`M 140 ${cy - nutOuter} Q 185 ${cy - nutOuter - 22} 230 ${cy - nutOuter}`}
          fill="none" stroke="#c45a10" strokeWidth="3"/>
        <text x={185} y={cy - nutOuter - 24} textAnchor="middle" fontSize="8.5" fill="#c45a10">Return tube</text>
        {/* Balls in groove (simplified row) */}
        {Array.from({length: 7}, (_, i) => (
          <circle key={i} cx={138 + i * 14} cy={cy - shaftR - 3} r={ballR}
            fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
        ))}
        {Array.from({length: 7}, (_, i) => (
          <circle key={i} cx={138 + i * 14} cy={cy + shaftR + 3} r={ballR}
            fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
        ))}

        {/* Annotations */}
        <text x={200} y={cy + 8} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Ball Nut</text>
        <text x={200} y={cy + 20} textAnchor="middle" fontSize="8.5" fill="#a04010">(recirculating)</text>
        <text x={80} y={cy - shaftR - 12} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Screw Shaft</text>

        {/* Pitch dimension */}
        <line x1={30} y1={cy + nutOuter + 14} x2={30 + 340 / 6} y2={cy + nutOuter + 14}
          stroke="#c45a10" strokeWidth="1.5" markerEnd="url(#arBS)" markerStart="url(#arBSr)"/>
        <text x={30 + 170 / 6} y={cy + nutOuter + 28} textAnchor="middle" fontSize="9" fontWeight="700" fill="#c45a10">Pitch p</text>

        {/* ── Cross-section (circle view, right side) ── */}
        <text x={cx + 240} y={cy - nutOuter - 22} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Cross-Section View</text>

        {/* Nut outer ring */}
        <circle cx={cx + 240} cy={cy} r={nutOuter + 12} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="2.5"/>
        {/* Nut inner (with thread groove) */}
        <circle cx={cx + 240} cy={cy} r={nutInner + 6} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2" opacity="0.6"/>
        {/* Shaft */}
        <circle cx={cx + 240} cy={cy} r={shaftR} fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5"/>
        <circle cx={cx + 240} cy={cy} r={shaftR - 5} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.5"/>
        {/* Balls (8 balls around PCD) */}
        {Array.from({length: 8}, (_, i) => {
          const a = (2 * Math.PI * i) / 8 + Math.PI / 8;
          const bx = cx + 240 + ballPcd * Math.cos(a);
          const by = cy + ballPcd * Math.sin(a);
          return <circle key={i} cx={bx} cy={by} r={ballR} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>;
        })}
        {/* Gothic arch contact angle annotation */}
        {(() => {
          const bx = cx + 240 + ballPcd * Math.cos(-Math.PI * 0.15);
          const by = cy + ballPcd * Math.sin(-Math.PI * 0.15);
          return (
            <g>
              <line x1={bx} y1={by} x2={bx + 28} y2={by - 18} stroke="#c45a10" strokeWidth="1.2"/>
              <text x={bx + 32} y={by - 20} fontSize="8.5" fill="#c45a10" fontWeight="700">Contact angle</text>
              <text x={bx + 32} y={by - 9} fontSize="8" fill="#a04010">α ≈ 45° (gothic arch)</text>
            </g>
          );
        })()}

        {/* ── Right panel: key specs and preload ── */}
        <rect x={640} y={15} width={308} height={374} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={794} y={33} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Ball Screw Key Parameters</text>
        <line x1={648} y1={39} x2={940} y2={39} stroke="#e8b07a"/>
        {[
          ['Nominal dia d₀:', '6–200 mm (standard)'],
          ['Pitch p:', '1–50 mm (typical 1–20)'],
          ['Lead accuracy:','C0–C10 (JIS) / P1–P5 (DIN)'],
          ['C3 / P3:','±0.012 mm / 300 mm travel'],
          ['C5 / P5:','±0.023 mm / 300 mm travel'],
          ['', ''],
          ['Preload methods:', ''],
          ['1. Double nut:', 'Two nuts + spacer/spring'],
          ['', 'Preload = 8–10% dynamic C'],
          ['2. Oversize balls:', 'Larger balls fill groove gap'],
          ['', 'Simple, single nut'],
          ['3. Lead offset:', 'Two halves with offset lead'],
          ['', 'No axial space needed'],
          ['', ''],
          ['Preload benefit:', 'Eliminates backlash'],
          ['', 'Increases stiffness'],
          ['Preload penalty:', 'Heat generation ↑'],
          ['', 'Life reduced ~50%'],
          ['', ''],
          ['DN value:', 'd₀(mm) × n(rpm) ≤ 70,000–150,000'],
          ['', '(shaft whip limit)'],
          ['Critical speed:', 'n_cr = f·d_r/L² (rpm)'],
          ['', 'f depends on end support'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={648} y={52 + i * 14} fontSize="9"
              fontWeight={l.includes('Preload') || l === 'Nominal dia d₀:' ? '700' : '400'}
              fill={l.includes('Preload') ? '#c45a10' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            <text x={l ? 752 : 648} y={52 + i * 14} fontSize="9"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Ball screws convert rotary to linear motion with 90–96% efficiency via recirculating balls — preload eliminates backlash at the cost of higher heat generation and ~50% life reduction</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Linear Guide Rail Anatomy
// ─────────────────────────────────────────────────────────────────────
function DiagramLinearRail() {
  const W = 1020, H = 380;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linear Guide Rail &amp; Carriage — Anatomy, Load Directions &amp; Accuracy Classes</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="arLR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* ── Rail cross-section (large, left side) ── */}
        {/* Rail base */}
        <rect x={80} y={210} width={200} height={30} rx={4} fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5"/>
        {/* Rail body */}
        <rect x={110} y={130} width={140} height={80} rx={4} fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5" opacity="0.85"/>
        {/* Ball grooves on sides */}
        <path d={`M 110 158 Q 120 150 130 158 Q 120 166 110 158`} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
        <path d={`M 110 182 Q 120 174 130 182 Q 120 190 110 182`} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
        <path d={`M 230 158 Q 220 150 210 158 Q 220 166 230 158`} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
        <path d={`M 230 182 Q 220 174 210 182 Q 220 190 230 182`} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={180} y={175} textAnchor="middle" fontSize="11" fontWeight="700" fill="#fdf4ec">RAIL</text>

        {/* Carriage block */}
        <rect x={80} y={80} width={200} height={52} rx={5} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2.5" opacity="0.9"/>
        {/* Ball circuit rows */}
        {[95, 145, 215, 265].map((bx, bi) => (
          <g key={bi}>
            <circle cx={bx} cy={bi < 2 ? 98 : 98} r={7} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
            <circle cx={bx} cy={118} r={7} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
          </g>
        ))}
        <text x={180} y={108} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">CARRIAGE</text>
        <text x={180} y={122} textAnchor="middle" fontSize="8.5" fill="#a04010">(ball bearing block)</text>

        {/* Load direction arrows */}
        {/* Radial (downward) */}
        <line x1={180} y1={60} x2={180} y2={78} stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arLR)"/>
        <text x={180} y={54} textAnchor="middle" fontSize="9" fontWeight="700" fill="#c45a10">F_radial</text>
        {/* Reverse radial (upward) */}
        <line x1={220} y1={78} x2={220} y2={60} stroke="#c45a10" strokeWidth="2" markerEnd="url(#arLR)"/>
        <text x={256} y={54} textAnchor="end" fontSize="8.5" fill="#c45a10">F_rev.radial</text>
        {/* Lateral */}
        <line x1={290} y1={106} x2={308} y2={106} stroke="#a04010" strokeWidth="2.5" markerEnd="url(#arLR)"/>
        <text x={312} y={110} fontSize="9" fontWeight="700" fill="#a04010">F_lateral</text>
        {/* Moment (roll) */}
        <path d={`M 70 90 A 20 20 0 0 0 70 122`} fill="none" stroke="#7a2e00" strokeWidth="2" markerEnd="url(#arLR)"/>
        <text x={40} y={108} textAnchor="middle" fontSize="8.5" fill="#7a2e00">M_roll</text>
        {/* Moment (pitch/yaw) */}
        <text x={180} y={250} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">M_pitch &amp; M_yaw</text>
        <text x={180} y={262} textAnchor="middle" fontSize="8.5" fill="#a04010">(taken by rail span)</text>

        {/* 45-degree contact angle annotation */}
        <line x1={110} y1={158} x2={92} y2={140} stroke="#c45a10" strokeWidth="1.2"/>
        <text x={88} y={136} textAnchor="end" fontSize="8" fill="#c45a10">45° contact</text>

        {/* Recirculation path annotation */}
        <path d={`M 97 80 Q 97 62 180 58 Q 263 62 263 80`}
          fill="none" stroke="#a04010" strokeWidth="1.5" strokeDasharray="4,2"/>
        <text x={180} y={56} textAnchor="middle" fontSize="7.5" fill="#a04010" dy="-2">Ball return path</text>

        {/* ── Right panel: specs ── */}
        <rect x={370} y={10} width={280} height={360} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={510} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Linear Rail Key Parameters</text>
        <line x1={378} y1={34} x2={642} y2={34} stroke="#e8b07a"/>
        {[
          ['Series (width):', '9, 12, 15, 20, 25, 30, 35, 45, 55 mm'],
          ['Carriage length:', 'Short / Standard / Long / Super-long'],
          ['', ''],
          ['Accuracy classes:', ''],
          ['H (Normal):', '±0.025 mm parallelism/300mm'],
          ['P (Precision):', '±0.015 mm'],
          ['SP (Super-P):', '±0.010 mm'],
          ['UP (Ultra-P):', '±0.005 mm'],
          ['', ''],
          ['Preload classes:', ''],
          ['Z0 (light):', 'Clearance / slight preload'],
          ['Z1 (normal):', '3–5% dynamic C (C₀)'],
          ['Z2 (medium):', '6–9% dynamic C'],
          ['Z3 (heavy):', '10–13% dynamic C'],
          ['', ''],
          ['Load ratings:', ''],
          ['Dynamic C:', 'L = (C/P)³ × 50 km'],
          ['Static C₀:', 'Must exceed peak load'],
          ['', '(with safety factor 1.5–3)'],
          ['', ''],
          ['Contact angle:', '45° (4-row gothic arch)'],
          ['', 'Equal load in all 4 directions'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={378} y={46 + i * 14} fontSize="9"
              fontWeight={l.includes('class') || l.includes('rating') ? '700' : '400'}
              fill={l.includes('class') || l.includes('rating') ? '#c45a10' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            <text x={l ? 465 : 378} y={46 + i * 14} fontSize="9"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}

        {/* ── Right-right panel: arrangement types ── */}
        <rect x={665} y={10} width={344} height={360} rx={6} fill="#fdf4ec" stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={837} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Common Arrangements</text>
        <line x1={673} y1={34} x2={1001} y2={34} stroke="#e8b07a"/>

        {/* Draw 3 arrangement schematics */}
        {[
          { label: '1 Rail, 2 Carriages', y: 55, note: 'Simple · must guide\npitch & yaw from\nscrew/drive' },
          { label: '2 Rails, 1 Carriage ea.', y: 170, note: 'Most common · good\nmoment stiffness\n→ CNC axes, slides' },
          { label: '2 Rails, 2 Carriages ea.', y: 280, note: 'Highest stiffness\nOver-constrained unless\nrails very parallel' },
        ].map((arr, i) => {
          const ry = arr.y;
          const panelCx = 837; // centre of 344-wide panel from x=665
          return (
            <g key={i}>
              <text x={panelCx} y={ry - 4} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">{arr.label}</text>
              {/* Rail schematics — all contained within x=673..1001 */}
              {i === 0 && (
                <g>
                  <rect x={680} y={ry} width={314} height={10} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
                  <rect x={740} y={ry - 18} width={44} height={20} rx={3} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
                  <rect x={850} y={ry - 18} width={44} height={20} rx={3} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
                </g>
              )}
              {i === 1 && (
                <g>
                  {[680, 840].map((rx2, ri) => (
                    <g key={ri}>
                      <rect x={rx2} y={ry} width={130} height={10} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
                      <rect x={rx2 + 40} y={ry - 18} width={50} height={20} rx={3} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
                    </g>
                  ))}
                </g>
              )}
              {i === 2 && (
                <g>
                  {[680, 840].map((rx2, ri) => (
                    <g key={ri}>
                      <rect x={rx2} y={ry} width={130} height={10} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
                      <rect x={rx2 + 10} y={ry - 18} width={44} height={20} rx={3} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
                      <rect x={rx2 + 76} y={ry - 18} width={44} height={20} rx={3} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
                    </g>
                  ))}
                </g>
              )}
              {arr.note.split('\n').map((line, li) => (
                <text key={li} x={panelCx} y={ry + 28 + li * 12} textAnchor="middle"
                  fontSize="8" fill="#7a4a28">{line}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Linear guides carry combined radial, reverse-radial, lateral and moment loads via four rows of 45° contact-angle balls — contact angle enables equal capacity in all four load directions</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Screw Mechanics: Force, Torque & Speed
// ─────────────────────────────────────────────────────────────────────
function DiagramMechanics() {
  const W = 960, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Screw Drive Mechanics — Force, Torque, Speed &amp; Power Relationships</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Left: force/torque diagram */}
        <rect x={10} y={10} width={310} height={318} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={165} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Force ↔ Torque Relationships</text>
        <line x1={18} y1={34} x2={312} y2={34} stroke="#e8b07a"/>

        {/* Centre: screw whip & critical speed */}
        <rect x={335} y={10} width={295} height={318} rx={6} fill="#fdf4ec" stroke="#e8b07a" strokeWidth="1.5"/>

        {/* Right: life calculation */}
        <rect x={645} y={10} width={304} height={318} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>

        {/* Force/torque data — rendered on top of left panel */}
        {[
          ['Linear force from torque:', ''],
          ['F = 2π·T·η / L', '(motor → linear)'],
          ['', ''],
          ['Torque from load force:', ''],
          ['T = F·L / (2π·η)', '(overcome F_load)'],
          ['', ''],
          ['Include inertia + friction:', ''],
          ['T_total = T_load + T_inertia + T_friction', ''],
          ['T_inertia = J_total · α', '(accel. torque)'],
          ['J_total = J_motor + J_screw', ''],
          ['  + J_load·(L/2π)²', '(reflected inertia)'],
          ['', ''],
          ['Linear speed:', ''],
          ['v = n · L', '(mm/s if L in mm)'],
          ['n = rotational speed (rev/s)', ''],
          ['L = lead (mm/rev)', ''],
          ['', ''],
          ['Power:', ''],
          ['P_mech = F · v', '(watts)'],
          ['P_input = P_mech / η', '(motor power)'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={18} y={46 + i * 12.5} fontSize="9"
              fontWeight={!v && l ? '700' : '400'}
              fill={!v && l ? '#c45a10' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            {v && <text x={l ? 240 : 18} y={46 + i * 12.5} fontSize="9"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>}
          </g>
        ))}

        {/* Centre panel titles + data */}
        <text x={482} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Critical Speed &amp; Column Load</text>
        <line x1={343} y1={34} x2={622} y2={34} stroke="#e8b07a"/>
        {[
          ['Critical speed (whip):', ''],
          ['n_cr = f₁ · d_r · 10⁷ / L²', '(rpm)'],
          ['', 'L = unsupported length (mm)'],
          ['', 'd_r = root (minor) dia (mm)'],
          ['', 'f₁: end-fixity factor'],
          ['', '  F-F (both fixed): f₁=21.9'],
          ['', '  F-S (one fixed): f₁=15.1'],
          ['', '  F-C (one free):  f₁= 3.6'],
          ['', ''],
          ['Operating speed:', 'n_op ≤ 0.8 · n_cr'],
          ['', '(20% safety margin)'],
          ['', ''],
          ['Column load (buckling):', ''],
          ['F_cr = f₂ · E·I·π² / L²', '(Euler buckling)'],
          ['', 'f₂: end-fixity factor'],
          ['', '  F-F: f₂ = 4'],
          ['', '  F-S: f₂ = 2'],
          ['', '  F-F (guided): f₂ = 1'],
          ['', ''],
          ['Allowable thrust:', 'F ≤ 0.5 · F_cr'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={343} y={46 + i * 12.5} fontSize="9"
              fontWeight={!v && l ? '700' : '400'}
              fill={!v && l ? '#7a2e00' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            {v && <text x={l ? 480 : 343} y={46 + i * 12.5} fontSize="9"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>}
          </g>
        ))}

        {/* Right: life calculation — title + separator */}
        <text x={797} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Life Calculation (Ball Screw)</text>
        <line x1={653} y1={34} x2={941} y2={34} stroke="#e8b07a"/>
        {[
          ['L10 life (distance):', ''],
          ['L₁₀ = (C/P)³ × 50 km', '(basic rating life)'],
          ['C = dynamic load rating (N)', ''],
          ['P = equivalent dynamic load (N)', ''],
          ['', ''],
          ['Life in hours:', ''],
          ['L_h = L₁₀ / (v · 60 · 10⁻³)', ''],
          ['v = linear speed (m/min)', ''],
          ['', ''],
          ['Adjusted life:', ''],
          ['L_na = a₁ · a₂ · a₃ · L₁₀', ''],
          ['a₁: reliability factor', ''],
          ['  90% = 1.00  ·  95% = 0.62', ''],
          ['a₂: material factor (~1.0)', ''],
          ['a₃: operating condition (~0.5–1)', ''],
          ['', ''],
          ['Static safety factor:', ''],
          ['f_s = C₀ / P₀ ≥ 1.0–3.0', ''],
          ['(higher for shock loads)', ''],
          ['', ''],
          ['Preload heat gen.:', 'Q = T_preload · ω (W)'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={653} y={46 + i * 12.5} fontSize="9"
              fontWeight={!v && l ? '700' : '400'}
              fill={!v && l ? '#7a2e00' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            {v && <text x={780} y={46 + i * 12.5} fontSize="9"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Screw drive mechanics couples rotary and linear domains — reflected inertia of the load appears at the motor shaft scaled by (lead/2π)², making low-lead screws very effective at isolating motor from payload inertia</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Accuracy, Backlash & Preload
// ─────────────────────────────────────────────────────────────────────
function DiagramAccuracy() {
  const W = 960, H = 300;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Positioning Accuracy — Backlash, Preload &amp; Error Sources</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="arAC" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arACr" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
            <polygon points="6 0,0 3.5,6 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* ── Backlash diagram ── */}
        <rect x={10} y={10} width={280} height={278} rx={6} fill="none" stroke="#e8b07a" strokeDasharray="5,3" strokeWidth="1.5"/>
        <text x={150} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Backlash Effect</text>
        <text x={150} y={40} textAnchor="middle" fontSize="8.5" fill="#a04010">Dead band on direction reversal</text>

        {/* Position vs command chart */}
        <line x1={30} y1={68} x2={30} y2={200} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={30} y1={200} x2={260} y2={200} stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={26} y={72} textAnchor="end" fontSize="8" fill="#7a4a28">Pos</text>
        <text x={262} y={204} fontSize="8" fill="#7a4a28">Cmd</text>

        {/* Ideal line */}
        <line x1={30} y1={200} x2={240} y2={80} stroke="#e8c89a" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={242} y={78} fontSize="8" fill="#7a4a28">Ideal</text>

        {/* Actual with backlash */}
        <polyline points="30,200 120,130 140,130 240,80"
          fill="none" stroke="#c45a10" strokeWidth="2.5"/>
        {/* Backlash gap annotation */}
        <line x1={120} y1={130} x2={140} y2={130} stroke="#c0392b" strokeWidth="2.5"
          markerEnd="url(#arAC)" markerStart="url(#arACr)"/>
        <text x={130} y={122} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#c0392b">backlash</text>
        <text x={130} y={143} textAnchor="middle" fontSize="8" fill="#c0392b">dead zone</text>

        <text x={150} y={220} textAnchor="middle" fontSize="8.5" fill="#7a4a28">Reversal error = backlash value</text>
        <text x={150} y={232} textAnchor="middle" fontSize="8.5" fill="#7a4a28">CNC compensation possible</text>
        <text x={150} y={244} textAnchor="middle" fontSize="8.5" fill="#a04010">but not perfectly reliable</text>
        <text x={150} y={258} textAnchor="middle" fontSize="8.5" fill="#7a4a28">Solution: preload eliminates</text>
        <text x={150} y={270} textAnchor="middle" fontSize="8.5" fill="#c45a10">backlash mechanically</text>

        {/* ── Preload effect diagram ── */}
        <rect x={305} y={10} width={280} height={278} rx={6} fill="none" stroke="#c45a10" strokeDasharray="5,3" strokeWidth="1.8"/>
        <text x={445} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Preload Effect</text>
        <text x={445} y={40} textAnchor="middle" fontSize="8.5" fill="#c45a10">Eliminates backlash · increases stiffness</text>

        {/* Double nut schematic */}
        {(() => {
          const cx = 445, cy = 120, r = 16;
          return (
            <g>
              {/* Shaft */}
              <rect x={320} y={cy - r} width={250} height={r * 2} rx={r} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
              {/* Nut 1 */}
              <rect x={355} y={cy - r - 14} width={60} height={(r + 14) * 2} rx={4}
                fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
              <text x={385} y={cy + 5} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">N1</text>
              {/* Spacer */}
              <rect x={415} y={cy - 6} width={22} height={12} rx={2}
                fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
              <text x={426} y={cy + 4} textAnchor="middle" fontSize="7.5" fill="#fdf4ec">sp</text>
              {/* Nut 2 */}
              <rect x={437} y={cy - r - 14} width={60} height={(r + 14) * 2} rx={4}
                fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
              <text x={467} y={cy + 5} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">N2</text>
              {/* Preload force arrows between nuts */}
              <line x1={408} y1={cy - 22} x2={425} y2={cy - 22} stroke="#c45a10" strokeWidth="2"
                markerEnd="url(#arAC)"/>
              <line x1={444} y1={cy - 22} x2={427} y2={cy - 22} stroke="#c45a10" strokeWidth="2"
                markerEnd="url(#arAC)"/>
              <text x={426} y={cy - 26} textAnchor="middle" fontSize="8" fill="#c45a10">F_preload</text>
            </g>
          );
        })()}

        {/* Stiffness chart */}
        <text x={445} y={178} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Axial Stiffness vs Preload</text>
        <line x1={325} y1={195} x2={325} y2={255} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={325} y1={255} x2={555} y2={255} stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={321} y={197} textAnchor="end" fontSize="8" fill="#7a4a28">K_a</text>
        <text x={556} y={258} fontSize="8" fill="#7a4a28">Preload %</text>
        {(() => {
          const pts = Array.from({length: 30}, (_, i) => {
            const t = i / 29;
            const x = 325 + t * 220;
            const y = 255 - (30 + 50 * (1 - Math.exp(-4 * t)));
            return `${x},${y}`;
          }).join(' ');
          return <polyline points={pts} fill="none" stroke="#c45a10" strokeWidth="2.5"/>;
        })()}
        <text x={440} y={248} textAnchor="middle" fontSize="8" fill="#7a4a28">More preload → stiffer, but more heat</text>

        {/* ── Error sources panel ── */}
        <rect x={600} y={10} width={348} height={278} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={774} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Positioning Error Sources</text>
        <line x1={608} y1={34} x2={940} y2={34} stroke="#e8b07a"/>
        {[
          ['Lead error:', 'Screw pitch variation along length'],
          ['', 'C3 grade: ≤0.012mm per 300mm travel'],
          ['', 'Correctable by CNC pitch error comp.'],
          ['', ''],
          ['Thermal expansion:', 'ΔL = L · α · ΔT'],
          ['', 'Steel: α = 11.7 × 10⁻⁶ /°C'],
          ['', '1m screw, 5°C rise → 0.059 mm error'],
          ['', 'Fix: cooling, prestretch, thermal comp'],
          ['', ''],
          ['Backlash:', 'Direction-reversal dead band'],
          ['', 'Ball screw: 0–0.05mm unpre-loaded'],
          ['', 'Preloaded ball screw: ~0 mm'],
          ['', ''],
          ['Elastic deflection:', 'F_axial / K_screw (stiffness)'],
          ['', 'K_screw = E·A / L (axial)'],
          ['', 'Must include nut & bearing compliance'],
          ['', ''],
          ['Measurement note:', 'Accuracy class (lead error only)'],
          ['', '≠ positioning accuracy (all sources)'],
          ['', 'System accuracy = √(sum of squares)'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={608} y={46 + i * 12.5} fontSize="9"
              fontWeight={!v && l ? '700' : '400'}
              fill={!v && l ? '#c45a10' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            <text x={l ? 700 : 608} y={46 + i * 12.5} fontSize="9"
              fontFamily={v ? "'Courier New',monospace" : undefined} fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>System positioning accuracy combines lead error, thermal expansion, elastic deflection, and backlash — preload eliminates backlash but thermal effects often dominate in precision machines</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — End Bearing Configurations
// ─────────────────────────────────────────────────────────────────────
function DiagramEndBearings() {
  const W = 960, H = 310;

  const EndConfig = ({ x, y, label, fixed1, fixed2, note, critF }) => {
    const shaftL = 200;
    return (
      <g>
        <text x={x + shaftL / 2} y={y - 8} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">{label}</text>
        {/* Shaft */}
        <rect x={x} y={y} width={shaftL} height={12} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.8"/>
        {/* Left bearing */}
        <rect x={x - 14} y={y - 8} width={18} height={28} rx={3}
          fill={fixed1 ? '#c45a10' : '#e8b07a'} stroke="#7a2e00" strokeWidth="1.8" opacity="0.9"/>
        <text x={x + 2} y={y + 38} textAnchor="middle" fontSize="8.5"
          fill={fixed1 ? '#c45a10' : '#a04010'}>{fixed1 ? 'Fixed' : 'Float'}</text>
        {/* Right bearing */}
        <rect x={x + shaftL - 4} y={y - 8} width={18} height={28} rx={3}
          fill={fixed2 ? '#c45a10' : '#e8b07a'} stroke="#7a2e00" strokeWidth="1.8" opacity="0.9"/>
        <text x={x + shaftL + 6} y={y + 38} textAnchor="middle" fontSize="8.5"
          fill={fixed2 ? '#c45a10' : '#a04010'}>{fixed2 ? 'Fixed' : 'Float'}</text>
        {/* Ground symbols for fixed */}
        {fixed1 && (
          <g>
            {[0, 5, 10].map((dy, di) => (
              <line key={di} x1={x - 20 + di * 2} y1={y - 12} x2={x - 24 + di * 2} y2={y - 20}
                stroke="#7a2e00" strokeWidth="1.2"/>
            ))}
            <line x1={x - 22} y1={y - 20} x2={x - 14} y2={y - 20} stroke="#7a2e00" strokeWidth="1.5"/>
          </g>
        )}
        {fixed2 && (
          <g>
            {[0, 5, 10].map((dy, di) => (
              <line key={di} x1={x + shaftL + 12 + di * 2} y1={y - 12} x2={x + shaftL + 8 + di * 2} y2={y - 20}
                stroke="#7a2e00" strokeWidth="1.2"/>
            ))}
            <line x1={x + shaftL + 10} y1={y - 20} x2={x + shaftL + 22} y2={y - 20} stroke="#7a2e00" strokeWidth="1.5"/>
          </g>
        )}
        {/* Notes */}
        {note.split('\n').map((line, li) => (
          <text key={li} x={x + shaftL / 2} y={y + 54 + li * 13} textAnchor="middle"
            fontSize="8" fill="#7a4a28">{line}</text>
        ))}
        {/* Critical speed factor */}
        <rect x={x + shaftL / 2 - 32} y={y + 90} width={64} height={18} rx={3}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
        <text x={x + shaftL / 2} y={y + 103} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#c45a10">{critF}</text>
      </g>
    );
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>End Bearing Configurations — Fixed-Free, Fixed-Simple, Fixed-Fixed</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        <EndConfig x={30} y={120} label="Fixed-Free (F-F)"
          fixed1={true} fixed2={false}
          note={"Simple · long overhang\nLowest critical speed\nSmall machines, short travel"}
          critF="f₁=3.6 · f₂=0.25"/>

        <EndConfig x={270} y={120} label="Fixed-Simple (F-S)"
          fixed1={true} fixed2={false}
          note={"Most common\nRight end: radial only\nGood balance of cost/perf"}
          critF="f₁=15.1 · f₂=2"/>

        <EndConfig x={510} y={120} label="Fixed-Fixed (F-F*)"
          fixed1={true} fixed2={true}
          note={"Highest critical speed\nPrestretch screw thermally\nLong, high-speed axes"}
          critF="f₁=21.9 · f₂=4"/>

        {/* Right panel: summary */}
        <rect x={745} y={15} width={204} height={280} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={847} y={32} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">End Support Summary</text>
        <line x1={753} y1={38} x2={941} y2={38} stroke="#e8b07a"/>
        {[
          ['Fixed end:', 'Angular contact bearing pair'],
          ['', '(face-to-face or back-to-back)'],
          ['', 'Takes axial + radial load'],
          ['', ''],
          ['Simple/floating:', 'Deep groove ball bearing'],
          ['', 'Radial load only'],
          ['', 'Allows thermal expansion'],
          ['', ''],
          ['Prestretch:', 'Screw compressed before'],
          ['', 'mounting at Fixed-Fixed'],
          ['', 'Compensates thermal exp.'],
          ['', 'ΔL_pre ≈ L·α·ΔT_expected'],
          ['', ''],
          ['Bearing choice:', ''],
          ['Fixed end:', '7000 series AC bearings'],
          ['', 'or matched duplex pair'],
          ['Float end:', '6000 series (DGBB)'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={753} y={50 + i * 14} fontSize="8.5"
              fontWeight={l && !v ? '700' : '400'}
              fill={l === 'Fixed end:' || l === 'Simple/floating:' || l === 'Prestretch:' ? '#c45a10' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            <text x={l ? 810 : 753} y={50 + i * 14} fontSize="8.5"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>End bearing configuration determines critical speed, buckling load, and thermal compensation strategy — Fixed-Fixed requires a pre-stretch to avoid thermal buckling</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 8 — Lubrication, Maintenance & Failure Modes
// ─────────────────────────────────────────────────────────────────────
function DiagramMaintenance() {
  const W = 960, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Lubrication, Maintenance Schedule &amp; Failure Modes</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Lubrication panel */}
        <rect x={10} y={10} width={290} height={298} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={155} y={27} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Lubrication</text>
        <line x1={18} y1={33} x2={292} y2={33} stroke="#e8b07a"/>

        {/* Failure modes panel */}
        <rect x={315} y={10} width={330} height={298} rx={6} fill="#fdf4ec" stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={480} y={27} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Failure Modes</text>
        <line x1={323} y1={33} x2={637} y2={33} stroke="#e8b07a"/>

        {/* Inspection panel */}
        <rect x={660} y={10} width={290} height={298} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={805} y={27} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Inspection &amp; Replacement</text>
        <line x1={668} y1={33} x2={942} y2={33} stroke="#e8b07a"/>
        {[
          ['Ball Screw — Grease:', ''],
          ['Type:', 'Li-based NLGI 2 or special'],
          ['', '(e.g. Mobilux EP2, Isoflex)'],
          ['Fill quantity:', '30–50% of nut cavity volume'],
          ['Interval:', '500–2,000 h or 100km travel'],
          ['', ''],
          ['Ball Screw — Oil:', ''],
          ['Type:', 'ISO VG 32–68 way oil'],
          ['Method:', 'Mist, drip, or circulation'],
          ['Used when:', 'High speed (DmN > 80,000)'],
          ['', ''],
          ['Lead Screw:', ''],
          ['Type:', 'Heavy PTFE grease or MoS₂'],
          ['Interval:', '200–500 h (more friction)'],
          ['', ''],
          ['Linear Rail:', ''],
          ['Type:', 'ISO VG 32–68, NLGI 2 grease'],
          ['Method:', 'Grease nipple on carriage'],
          ['Interval:', '500–2,000 h; check wiper seals'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={18} y={44 + i * 12} fontSize="9"
              fontWeight={l.includes('—') || l === 'Ball Screw — Grease:' ? '700' : '400'}
              fill={l.includes('—') ? '#c45a10' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            <text x={l ? 100 : 18} y={44 + i * 12} fontSize="9"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}

        {/* Lubrication data */}
        {[
          ['Wear (gradual):', 'Increasing backlash + noise'],
          ['', 'Ball groove pitting (Hertz fatigue)'],
          ['', 'Lead error growth over life'],
          ['Prevention:', 'Correct lubrication + load'],
          ['', ''],
          ['Brinelling / denting:', 'Static overload → dents in groove'],
          ['', 'Check static C₀ rating carefully'],
          ['Prevention:', 'Use f_s ≥ 3 for shock loads'],
          ['', ''],
          ['Corrosion:', 'Rust pitting → spalling → failure'],
          ['', 'Contamination accelerates this'],
          ['Prevention:', 'Seals, correct lube, environment'],
          ['', ''],
          ['Whip (vibration):', 'n > n_cr → severe vibration'],
          ['Prevention:', 'n_op ≤ 0.8·n_cr ; use large dia'],
          ['', ''],
          ['Nut failure:', 'Preload loss → backlash return'],
          ['', 'Ball loss → catastrophic seizure'],
          ['Prevention:', 'Replace nut at life limit'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={323} y={44 + i * 12} fontSize="9"
              fontWeight={l.includes(':') && !l.includes('→') ? '700' : '400'}
              fill={l === 'Prevention:' ? '#27ae60' : l.includes(':') ? '#c0392b' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            <text x={l ? 415 : 323} y={44 + i * 12} fontSize="9"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}

        {/* Failure modes data */}
        {[
          ['Backlash check:', 'Dial indicator; reverse direction'],
          ['', 'Ball screw: accept < 0.02 mm'],
          ['', 'Lead screw: accept < 0.1 mm'],
          ['', ''],
          ['Noise check:', 'Irregular rattling → ball damage'],
          ['', 'Grinding → contamination'],
          ['', 'Rumble → groove wear'],
          ['', ''],
          ['Temperature:', 'Abnormal rise → lube failure'],
          ['', 'or overloading'],
          ['', ''],
          ['Life monitoring:', 'Log km travelled or rev count'],
          ['', 'Replace at 80% of rated L10'],
          ['', ''],
          ['Replacement signs:', ''],
          ['', '• Backlash > spec'],
          ['', '• Abnormal noise / vibration'],
          ['', '• Positional error growing'],
          ['', '• Temperature rise > 20°C'],
          ['', '• Visual: rust, pitting, chips'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={668} y={44 + i * 12} fontSize="9"
              fontWeight={!v && l ? '700' : '400'}
              fill={!v && l && l.includes(':') ? '#c45a10' : l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            <text x={l ? 760 : 668} y={44 + i * 12} fontSize="9"
              fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Regular lubrication is the single most important maintenance action — most ball screw and linear rail failures are due to insufficient or incorrect lubrication, not wear-out under normal loads</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 9 — Applications
// ─────────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W = 900, H = 376;
  const apps = [
    { title: 'CNC Machine\nTool Axes', icon: '⚙️', lines: ['Ball screw + linear rail', 'C3/C5 accuracy class', 'Servo motor drive', 'Rapid traverse 20–60 m/min'] },
    { title: '3D Printer\n& Laser Cutter', icon: '🔬', lines: ['Lead screw or belt drive', 'Tr8 / Tr10 typical', 'Low cost, open loop OK', 'Low speeds acceptable'] },
    { title: 'Industrial\nRobot Linear', icon: '🤖', lines: ['Ball screw + LM guide', 'High speed, high duty', 'Servo + encoder feedback', 'Long stroke axes'] },
    { title: 'Aerospace &\nFlight Test', icon: '✈️', lines: ['Precision ball screws', 'Actuators, test rigs', 'Preloaded for zero lash', 'High stiffness critical'] },
    { title: 'Semiconductor\nEquipment', icon: '💿', lines: ['Ultra-precision screws', 'Sub-micron positioning', 'Low-outgassing lube', 'Clean room compatible'] },
    { title: 'Medical\nDevices', icon: '🏥', lines: ['Lead or ball screw', 'Surgical tables, MRI', 'Self-locking for safety', 'Stainless, sterilisable'] },
  ];
  const cardH = 148, rowGap = 22;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linear Motion System Applications Across Engineering</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {apps.map((a, i) => {
          const col = i % 3, row = Math.floor(i / 3);
          const x = 80 + col * 285, y = 18 + row * (cardH + rowGap);
          const lines = a.title.split('\n');
          return (
            <g key={i}>
              <rect x={x - 65} y={y} width={146} height={cardH} rx={6}
                fill={i % 2 === 0 ? '#fdf4ec' : '#fdf0e4'} stroke="#e8b07a" strokeWidth="1.5"/>
              <circle cx={x} cy={y + 32} r={26} fill="#c45a10" opacity="0.85" stroke="#7a2e00" strokeWidth="2"/>
              <text x={x} y={y + 39} textAnchor="middle" fontSize="18">{a.icon}</text>
              {lines.map((l, li) => (
                <text key={li} x={x} y={y + 68 + li * 14} textAnchor="middle"
                  fontSize="10" fontWeight="700" fill="#7a2e00">{l}</text>
              ))}
              <line x1={x - 55} y1={y + 96} x2={x + 55} y2={y + 96} stroke="#e8c89a"/>
              {a.lines.map((l, li) => (
                <text key={li} x={x} y={y + 108 + li * 12} textAnchor="middle"
                  fontSize="8" fill="#a04010">{l}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Ball screw + linear rail is the dominant combination for servo-driven precision axes · lead screws remain popular for simple, self-locking, or cost-sensitive applications</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────
export default function LinearMotionSystems() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Linear Motion Systems in Mechanical Engineering</h1>
      <p style={S.p}>
        Linear motion systems convert rotary motion into precise linear displacement using <strong>screws</strong> and
        guide it accurately with <strong>linear rails</strong>. Three technologies dominate: the <em>lead screw</em>
        (simple, self-locking, low cost), the <em>ball screw</em> (high efficiency, precision, high speed), and the
        <em> linear guide rail</em> (high stiffness, accuracy, load capacity in all directions). In modern CNC
        machines, robots, and precision instruments, ball screws and linear rails work together — the screw drives
        the axis while the rail constrains all non-driving degrees of freedom with micrometric accuracy.
      </p>
<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                    <img src={LinearMotionImg} alt="linear motion assemblies" width="55%"/>
                                </div>
      <InfoBox title="Quick Reference — Core Formulae &amp; Key Parameters" items={[
        "Linear travel per rev:  v = n · L  where L = lead (mm/rev), n = rotational speed (rev/s)",
        "Torque to move load:  T = F·L / (2π·η)  where F = axial force (N), η = screw efficiency",
        "Axial force from torque:  F = 2π·T·η / L",
        "Lead screw efficiency:  η_f = tan(λ) / tan(λ + φ)  · φ = arctan(μ/cos(α))  · λ = lead angle",
        "Self-locking condition:  λ < φ  (lead angle less than friction angle)",
        "Ball screw L10 life:  L₁₀ = (C/P)³ × 50 km  · C = dynamic load rating · P = equivalent load",
        "Critical speed:  n_cr = f₁ · d_r · 10⁷ / L²  (rpm)  · f₁ depends on end support configuration",
        "Column buckling load:  F_cr = f₂ · E·I·π² / L²  (Euler)  · operating thrust ≤ 0.5·F_cr",
        "Thermal expansion:  ΔL = L · α · ΔT  · steel α = 11.7 × 10⁻⁶ /°C",
        "Reflected inertia of load:  J_reflected = m_load × (L/2π)²  — appears at motor shaft",
        "Linear rail life:  L₁₀ = (C/P)³ × 50 km  (same formula as ball screw)",
        "Output pin hole (ball screw preload):  Preload ≈ 8–10% of dynamic C rating",
      ]}/>

      <h2 style={S.h2}>System Comparison — Lead Screw vs Ball Screw vs Linear Rail</h2>
      <DiagramComparison />
      <Table2 rows={[
        ['Lead screw', 'Simple threaded screw + sliding nut (bronze, PTFE, acetal)\nHigh friction → self-locking, no brake needed\nEfficiency 25–85% depending on lead angle and lubrication\nLow cost · ideal for slow, intermittent, low-precision applications'],
        ['Ball screw', 'Threaded shaft with recirculating balls between screw and nut\nHigh efficiency (90–96%) due to rolling contact\nNot self-locking — requires motor brake or electromechanical lock\nThe precision standard for CNC, robotics, and automation'],
        ['Linear guide rail', 'Hardened steel rail with 4-row gothic-arch ball circuit in carriage\nCarries loads in radial, reverse-radial, lateral, pitch, roll, yaw\nDoes not drive motion — paired with screw or belt drive\nExtremely high stiffness and accuracy in a compact form factor'],
        ['Choosing between them', 'Lead screw: when self-locking, low cost, simple, low speed required\nBall screw: when high efficiency, speed, accuracy, or duty cycle required\nLinear rail: always used with either screw type for accurate guidance\nBelt drive: alternative for long travel, high speed, lower precision axes'],
      ]}/>

      <h2 style={S.h2}>Lead Screw — Anatomy, Thread Profiles &amp; Self-Locking</h2>
      <p style={S.p}>
        Lead screws rely on a threaded shaft meshing with a matching nut. The relative motion converts rotation to
        translation with a mechanical advantage determined by the lead and thread geometry. Unlike ball screws, the
        contact is sliding — creating friction that both limits efficiency and enables self-locking, a property
        essential for many applications such as positioning tables, jacks, and valve actuators.
      </p>
      <DiagramLeadScrew />
      <Table2 rows={[
        ['Lead vs pitch', 'Pitch = distance between adjacent thread crests\nLead = axial travel per one full revolution = pitch × number of starts\nSingle-start: lead = pitch · Multi-start: lead > pitch (faster travel, steeper angle)'],
        ['ACME thread (29°)', 'Most common lead screw thread profile in imperial design\n29° included flank angle · trapezoidal cross-section\nGood strength, machinable · standard nut materials: bronze, PTFE, acetal'],
        ['Metric trapezoidal (Tr)', 'ISO standard equivalent to ACME · 30° included angle\nDesignated Tr8×2 (8mm diameter, 2mm pitch) etc.\nCommon in European and precision equipment'],
        ['Square thread', 'Theoretically highest efficiency (no radial load component)\nVery difficult to machine accurately · not self-aligning\nRare in practice — ACME/Tr used instead'],
        ['Self-locking derivation', 'A screw is self-locking when the lead angle λ < friction angle φ\nφ = arctan(μ_eff) where μ_eff = μ/cos(α) (half-flank angle correction)\nTypical ACME + bronze nut: μ ≈ 0.08–0.15 → self-locking at λ < 4.5°–8.5°'],
        ['Nut materials', 'Bronze: best wear resistance · good for continuous duty\nAcetal/Delrin: low friction, light loads · self-lubricating\nPTFE-impregnated: lowest friction, moderate loads\nAntibacklash nuts: spring-loaded halves eliminate axial play'],
      ]}/>

      <h2 style={S.h2}>Ball Screw — Anatomy, Accuracy Classes &amp; Preload</h2>
      <DiagramBallScrew />
      <Table2 rows={[
        ['Operating principle', 'Steel balls circulate in a helical groove between screw shaft and nut\nBalls leave the loaded groove, travel through a return tube, re-enter\nRolling contact reduces friction coefficient from ~0.1 (lead screw) to ~0.003'],
        ['Gothic arch groove', 'Ball groove has a gothic-arch cross-section (two arcs meeting at a cusp)\nThis creates two contact points per ball at ~45° contact angle\nEnables both axial and radial load capacity from a single groove row'],
        ['Accuracy classes', 'JIS C0–C10 or DIN P1–P10 (higher number = lower accuracy)\nClass C3/P3: ≤0.012mm lead error per 300mm — precision machine tools\nClass C5/P5: ≤0.023mm — general CNC · C7/P7: ≤0.05mm — general use'],
        ['Preload methods', 'Double nut + spacer: two nuts with precision spacer between them (common)\nOversize balls: slightly larger balls fill the radial clearance (simple, compact)\nLead offset: two nut halves with deliberately offset leads (no extra parts)\nPreload ≈ 8–10% of dynamic C rating is typical; higher → more heat'],
        ['DN value limit', 'DN = d₀(mm) × n(rpm) — limits shaft whip and ball centrifugal forces\nGround ball screws: DN ≤ 70,000 (precision) to 150,000 (high-speed designs)\nExceeding DN limit → vibration, ball skidding, rapid wear'],
        ['Accuracy vs rolled vs ground', 'Ground screws: highest accuracy (C3/C5), thread ground after hardening\nRolled screws: cold-formed, less accurate (C7–C10) but cheaper and faster\nRolled can achieve C5 with measurement and selection, not guaranteed'],
      ]}/>

      <h2 style={S.h2}>Linear Guide Rail — Anatomy, Load Capacity &amp; Accuracy</h2>
      <DiagramLinearRail />
      <Table2 rows={[
        ['4-row ball circuit', 'Four rows of balls (2 per side) in gothic-arch grooves at 45° contact angle\n45° contact gives equal load capacity in radial, reverse-radial, and lateral directions\nBalls circulate through return channels inside the carriage body'],
        ['Contact angle significance', '45° contact angle (normal) → equal 4-direction capacity\n15° (angular) contact → higher radial but lower lateral capacity (some designs)\nContact angle also determines moment load capacity via effective load centre'],
        ['Preload classes', 'Z0: clearance (backlash) — for light, non-reversal applications\nZ1 (3–5% C): standard precision machine tool use\nZ2 (6–9% C): high rigidity required — heavy cutting, high dynamic loads\nZ3 (10–13% C): maximum stiffness — highest precision, most heat generated'],
        ['Accuracy classes', 'H (Normal) → P (Precision) → SP (Super-precision) → UP (Ultra-precision)\nParallelism of carriage running surface to rail mounting surface\nUP class: ±0.003–0.005mm per 300mm — semiconductor, metrology equipment'],
        ['Moment load capacity', 'Single carriage: limited moment capacity (short contact footprint)\nTwo carriages per rail: moment capacity increases with carriage spacing\nTwo rails + two carriages each: highest moment stiffness — preferred for CNC axes'],
        ['Wiper seals and end seals', 'Wipers at each end of carriage remove chips and contamination from rail\nSeal quality is critical — contamination is the primary cause of rail failure\nFor harsh environments: bellows, telescopic covers, or air purge available'],
      ]}/>

      <h2 style={S.h2}>Mechanics — Force, Torque, Speed &amp; Life</h2>
      <DiagramMechanics />
      <Table2 rows={[
        ['Speed-to-force conversion', 'F = 2π·T·η / L — motor torque converts to linear force scaled by (2π/L)\nHigher lead L → less force per unit torque (faster but weaker)\nLower lead → more force per unit torque (slower but stronger)'],
        ['Reflected inertia', 'Load inertia at motor shaft: J_refl = m_load × (L/2π)²\nLow-lead screws dramatically reduce reflected inertia → motor-load matching\nTypical rule: J_load_reflected ≈ J_motor for optimal dynamic response'],
        ['Critical speed', 'Shaft whip occurs above critical speed — resonance of rotating shaft as a beam\nn_cr = f₁ × d_r × 10⁷ / L² (rpm)\nAlways operate below 0.8 × n_cr with a safety margin'],
        ['Buckling load', 'Long slender screws under compressive thrust can buckle (Euler buckling)\nF_cr = f₂ × E × I × π² / L² — I = π×d_r⁴/64 for solid shaft\nAllowable thrust = F_cr / safety factor (typically 2–4)'],
        ['L10 ball screw life', 'L₁₀ = (C/P)³ × 50 km — L₁₀ is distance at which 10% of population fails\nP = equivalent dynamic load (combine axial, preload, dynamic factors)\nLife in hours: L_h = L₁₀ / (v_linear in m/min × 60 × 10⁻³)'],
        ['Power and heat', 'Input power = F×v / η — power lost = F×v×(1−η)\nHeat = power loss → temperature rise → thermal expansion → position error\nFor high-duty applications: thermal management is a critical design constraint'],
      ]}/>

      <h2 style={S.h2}>Accuracy, Backlash &amp; Error Budget</h2>
      <DiagramAccuracy />
      <Table2 rows={[
        ['Backlash definition', 'Axial play between screw and nut — lost motion on direction reversal\nLead screw with plain nut: 0.05–0.5mm typical\nBall screw (unpreloaded): 0–0.05mm · Preloaded: essentially 0\nAnti-backlash nut (lead screw): spring-loaded to eliminate play'],
        ['Lead error', 'Deviation of actual screw pitch from nominal over a travel length\nExpressed as error per 300mm (JIS) or per 1000mm\nC3 ground screw: ≤0.012mm/300mm · C5: ≤0.023mm/300mm\nCNC can compensate via pitch error compensation table'],
        ['Thermal expansion error', 'ΔL = L × α × ΔT — often the largest error source in precision machines\n1m steel screw, 5°C rise → 0.059mm positioning error\nSolutions: temperature control, prestretch (F-F mounting), linear scale feedback'],
        ['Elastic deflection', 'Axial compliance = F / K_axial where K_axial = E×A/L + nut stiffness\nFor stiffness: short screws, large diameter, both ends fixed\nMust be included in servo loop model for high-bandwidth axes'],
        ['Total system accuracy', 'ε_total = √(ε_lead² + ε_thermal² + ε_deflection² + ε_backlash²)\nCannot simply add errors — root sum of squares for independent sources\nLinear encoder on table (instead of rotary on motor) eliminates most screw errors'],
        ['Linear encoder advantage', 'Encoder on moving table measures actual position, not motor rotation\nEliminates: lead error, thermal expansion of screw, backlash effects\nClosed-loop on actual position → system accuracy limited only by encoder + mounting'],
      ]}/>

      <h2 style={S.h2}>End Bearing Configurations</h2>
      <DiagramEndBearings />
      <Table2 rows={[
        ['Fixed-Free (F-F)', 'One end: angular contact bearing pair (takes thrust + radial)\nOther end: unsupported (free) — simplest and cheapest\nLowest critical speed and column load capacity\nUsed for short, slow, light-duty screws'],
        ['Fixed-Simple (F-S)', 'Fixed end: angular contact bearing pair\nSimple end: single deep-groove ball bearing (radial only, free axially)\nSimple end allows thermal expansion without inducing axial stress\nMost common configuration — good balance of performance and cost'],
        ['Fixed-Fixed (F-F*)', 'Both ends: angular contact bearing pairs\nHighest critical speed and column load capacity\nScrew must be prestretched during installation to compensate for thermal expansion\nUsed for long, high-speed axes: large CNC machines, high-speed machining centres'],
        ['Prestretch calculation', 'ΔL_prestretch = L_screw × α_steel × ΔT_expected\nExample: 1000mm screw, expected ΔT = 5°C → prestretch = 0.059mm\nApplied by tightening lock nut while screw is in tension at fixed ends'],
        ['Angular contact bearings', 'Paired back-to-back (DB) or face-to-face (DF) for fixed end\nDB pairing: better moment stiffness · DF: better for misalignment tolerance\nPre-adjusted (P) or matched (M) pairs for consistent preload without shimming'],
        ['Bearing preload', 'Fixed-end bearings must be preloaded to prevent axial play under reversal\nLight preload (G) for low-speed, high-accuracy\nMedium preload (M) for general CNC axes\nHeavy preload (H) for high-speed or heavy-duty axes'],
      ]}/>

      <h2 style={S.h2}>Lubrication, Maintenance &amp; Failure Modes</h2>
      <DiagramMaintenance />
      <Table2 rows={[
        ['Ball screw lubrication', 'Grease is most common: NLGI Grade 2 lithium-based or specialty\nFill 30–50% of nut cavity — overfilling causes churning and heat\nRelubrication every 500–2000h or 100km of travel (whichever first)\nHigh-speed (DmN > 80,000): oil mist or drip lubrication system'],
        ['Linear rail lubrication', 'Grease nipple on carriage block — inject until grease appears at wipers\nTypical: ISO VG 32–68 or NLGI Grade 2 every 500–2000h\nWiper seals are critical — replace when worn or hardened\nFor food/medical: NSF H1 approved food-grade lubrication'],
        ['Contamination control', 'Most linear motion failures are due to contamination, not wear-out\nChip and coolant ingress cuts into ball groove surfaces\nProvide covers: bellows, telescopic guards, air purge\nKeep rail and screw clean — wipe with lint-free cloth regularly'],
        ['Wear monitoring', 'Ball screw: check backlash with dial indicator at regular intervals\nLinear rail: measure carriage running accuracy with precision level\nAbnormal noise (grinding, rattling) → investigate immediately\nTemperature rise > 20°C above normal → lubrication or overload problem'],
        ['Service life and replacement', 'Ball screw rated L10 life: 50km×(C/P)³ — plan replacement before this\nActual service life often 5–10× rated life with good maintenance\nReplace at 80% of rated L10 or when backlash exceeds spec (whichever first)\nKeep replacement record — ball screw replacement is a major CNC service event'],
        ['Lead screw maintenance', 'More frequent lubrication needed (sliding contact, higher wear rate)\nMoS₂ grease or PTFE-impregnated lubricants reduce friction significantly\nCheck nut clearance regularly — PTFE nuts wear faster than bronze\nConsidering replacement when vertical axes lose self-locking property due to wear'],
      ]}/>

      <h2 style={S.h2}>System Integration — Motor Sizing &amp; Drive Design</h2>
      <Table2 rows={[
        ['Motor torque requirement', 'T_total = T_load + T_friction + T_inertia (during acceleration)\nT_load = F_axial × L / (2π × η)\nT_friction = includes nut friction, guide friction, seal drag\nT_inertia = J_total × α where α = angular acceleration (rad/s²)'],
        ['Inertia matching', 'Optimal: J_load_reflected ≈ J_motor (1:1 inertia ratio)\nHigh inertia ratio (> 10:1): sluggish response, poor bandwidth\nLow-lead screw reduces J_reflected = m × (L/2π)² — effective at low leads\nSafety: verify servo bandwidth > 10× mechanical resonance frequency'],
        ['Lead selection guide', 'For position accuracy: smaller lead → less linear error per rotary error\nFor speed: larger lead → more travel per motor revolution\nFor force: smaller lead → more force per unit torque\nTypical: start with L = v_max / n_motor and verify with force & stiffness check'],
        ['Linear encoder vs rotary', 'Rotary encoder on motor: simple, common — but screw errors enter loop\nLinear encoder on table: eliminates screw errors from feedback loop\nFor ≤ C5 accuracy: rotary encoder + pitch error compensation usually sufficient\nFor C3 or better: linear encoder strongly recommended'],
        ['Servo tuning notes', 'High-stiffness ball screw: higher P gain possible → faster response\nScrew resonance frequency: f_res ≈ (1/2π)×√(K_axial/m_total)\nAnti-resonance notch filter at f_res often needed in servo tuning\nBacklash in servo loop causes oscillation — always preload ball screw in closed-loop axes'],
        ['Thermal design', 'Power loss in screw: P_loss = F×v×(1−η) — goes into screw as heat\nBall screw preload adds more heat: Q_preload = T_preload × ω\nFor duty cycles > 40%: check temperature rise; consider screw cooling\nLinear encoder eliminates positional error from thermal expansion of screw shaft'],
      ]}/>

      <h2 style={S.h2}>Applications</h2>
      <DiagramApplications />
      <Table2 rows={[
        ['CNC machine tool axes', 'Ball screw C3/C5 + linear rail Z1/Z2 preload — the universal standard\nRapid traverse: 20–60 m/min (machining centre X/Y/Z axes)\nFeed: 1–15 m/min for cutting; servo motor + linear encoder feedback\nSpindle: separate ball screw for Z axis or built-in servo spindle'],
        ['3D printers & laser cutters', 'Lead screw (Tr8×8 or Tr8×2) for Z axis — self-locking holds bed position\nBelt + linear rail for X/Y axes (high speed, lower load, lower accuracy)\nLow-cost option: lead screw + 8mm linear shaft + LM8UU bearing (less stiff)\nHigh-end: ball screw + linear rail for professional FDM and SLA machines'],
        ['Industrial robots (linear)', 'Ball screw + LM guide for robot linear axes and 7th-axis tracks\nHigh duty cycle, high speed, moderate accuracy\nGantry robots: dual-rail for moment stiffness\nSCRA robot Z-axis: typically lead screw (self-locking safety)'],
        ['Semiconductor equipment', 'Ultra-precision ball screws C0–C3 grade\nSubmicron positioning accuracy with linear glass scales\nVacuum-compatible low-outgassing lubricants\nStage travel: 300–1000mm; speed < 1 m/s for air bearing stages'],
        ['Medical and surgical', 'Lead screws common for surgical tables, hospital beds (self-locking safety)\nBall screws in radiotherapy positioning tables\nElectric actuators: lead screw in acme nut (simple, redundant, self-locking)\nMRI compatibility: non-magnetic stainless steel / plastic components'],
        ['Linear actuators', 'Packaged unit: motor + gearbox + ball/lead screw + nut + housing\nElectric cylinder replaces hydraulic/pneumatic cylinder\nStroke: 25–1000mm; force: 100N–200kN depending on screw size\nSelf-locking lead screw critical for vertical and safety-critical actuators'],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Linear travel:  v = n · L  (mm/s)  ·  n = rev/s · L = lead mm/rev",
        "Torque to raise:  T = F · d_m/2 · tan(λ + φ)  ·  φ = arctan(μ/cos(α))",
        "Lead screw efficiency:  η = tan(λ) / tan(λ+φ)  ·  λ = arctan(L / π·d_m)",
        "Self-locking condition:  λ < φ  (lead angle < friction angle)",
        "Axial force:  F = 2π·T·η / L  ·  or  T = F·L / (2π·η)",
        "Reflected inertia:  J_refl = m_load × (L / 2π)²  (kg·m²)",
        "Critical speed:  n_cr = f₁ × d_r × 10⁷ / L²  (rpm)  ·  use 80% of n_cr",
        "Euler buckling:  F_cr = f₂ × E × π²×d_r⁴/64 / L²  (N)  ·  use 50% of F_cr",
        "Thermal expansion:  ΔL = L × 11.7×10⁻⁶ × ΔT  (m)  for steel",
        "Ball screw L10 life:  L₁₀ = (C/P)³ × 50 km  ·  replace before 80% of L10",
        "Linear rail L10 life:  L₁₀ = (C/P)³ × 50 km  (same formula)",
        "System accuracy:  ε = √(ε_lead² + ε_thermal² + ε_elastic² + ε_backlash²)",
      ]}/>
    </div>
  );
}